import { FC } from 'react';
import { useContextSelector as useCS } from 'use-context-selector';
import { Modal } from '@mui/material';
import { CloseIcon } from 'assets';
import { TUpdate } from 'domain/types';
import { Button, Input } from 'view/components';
import * as CS from 'view/modals/styled';
import {
  CreateJobPostConfigContext,
  TCreateJobPostContextState,
} from 'domain/createJobPostConfig/CreateJobPostContext';
import { createJobPostConfigSelectors } from 'domain/createJobPostConfig/selectors';
import { FormContainer } from 'react-hook-form-mui';
import { GlobalConfigContext } from 'domain/globalConfig/GlobalConfigContext';
import { globalConfigSelectors } from 'domain/globalConfig/selectors';
import { useApplyForJobPost } from 'view/pages/createJobPost/hooks/useApplyForJobPost';

interface IFormInput {
  initialMessage: string;
}

const JobSeekerApplyForJobPostModalFields = ({ onClose, isLoading }) => {
  return (
    <>
      <Input
        name="initialMessage"
        label="Short Message (Optional)"
        placeholder="E.g., “I’ve worked on several product design projects and would love to contribute to your team.”"
        multiline
        rows={6}
        marginbottom={32}
      />

      <CS.ModalMainFooter>
        <Button
          variant="outlined"
          text="Cancel"
          onClick={onClose}
          disabled={false}
          style={{ width: '100%', minHeight: '50px' }}
        />
        <Button variant="contained" text="Submit Application" type="submit" disabled={isLoading} />
      </CS.ModalMainFooter>
    </>
  );
};

const JobSeekerApplyForJobPostModal: FC<{
  hideCloseButton?: boolean;
  jobPostId: number;
  getJobPostInfoById: any;
}> = ({ hideCloseButton = false, jobPostId, getJobPostInfoById = () => {} }) => {
  const { jobSeekerProfile } = useCS(GlobalConfigContext, globalConfigSelectors.userData);
  const isOpenJobSeekerApplyForJobPostModal: boolean = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.isOpenJobSeekerApplyForJobPostModal
  );
  const update: TUpdate<TCreateJobPostContextState> | undefined = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.update
  );

  const { isLoading, applyForJobPost } = useApplyForJobPost();

  const onClose = () => {
    if (update) update({ isOpenJobSeekerApplyForJobPostModal: false });
  };

  const onSubmit = (formData: IFormInput) => {
    const dataToSend = {
      jobSeekerId: jobSeekerProfile.id,
      jobPostId,
      applicationType: 'APPLY',
      initialMessage: formData.initialMessage,
    };

    applyForJobPost(dataToSend, () => {
      onClose();
      getJobPostInfoById();
    });
  };

  const defaultValues = {
    initialMessage: '',
  };

  return (
    <Modal
      open={isOpenJobSeekerApplyForJobPostModal}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <CS.ModalMainWrapper>
        {!hideCloseButton ? (
          <CS.CloseIconBox onClick={onClose}>
            <CloseIcon />
          </CS.CloseIconBox>
        ) : null}
        <CS.ModalMainHeader style={{ marginBottom: '32px' }}>
          <CS.ModalMainHeaderTitle>Submit Your Application</CS.ModalMainHeaderTitle>
          <CS.ModalMainHeaderSubTitle>
            {
              'Add a short message to personalize your application. \n This is optional, but helps you stand out.'
            }
          </CS.ModalMainHeaderSubTitle>
        </CS.ModalMainHeader>
        <FormContainer
          key={JSON.stringify(defaultValues)}
          defaultValues={defaultValues}
          onSuccess={onSubmit}
        >
          <JobSeekerApplyForJobPostModalFields onClose={onClose} isLoading={isLoading} />
        </FormContainer>
      </CS.ModalMainWrapper>
    </Modal>
  );
};

export default JobSeekerApplyForJobPostModal;
